import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const transformForecastData = (forecastData: any) => {
  const rawData = forecastData?.cumulative_income || forecastData;
  if (!rawData) return [];

  const monthOrderMap = new Map([
    ['jan', 1],
    ['feb', 2],
    ['mar', 3],
    ['apr', 4],
    ['may', 5],
    ['jun', 6],
    ['jul', 7],
    ['aug', 8],
    ['sep', 9],
    ['oct', 10],
    ['nov', 11],
    ['dec', 12],
  ]);

  const formattedData: any = [];
  const years = Object.keys(rawData).sort((a, b) => Number(a) - Number(b));

  years.forEach((year) => {
    const monthsObj = rawData[year];
    if (!monthsObj) return;
    const sortedMonthKeys = Object.keys(monthsObj).sort((a, b) => {
      const aMonth = a.split('-')[0].toLowerCase();
      const bMonth = b.split('-')[0].toLowerCase();
      return (monthOrderMap.get(aMonth) || 0) - (monthOrderMap.get(bMonth) || 0);
    });

    sortedMonthKeys.forEach((monthKey) => {
      const metrics = monthsObj[monthKey];
      if (!metrics) return;

      // Format month name: "jan-26" → "Jan-26"
      const formattedName = monthKey
        .split('-')
        .map((part, i) => (i === 0 ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() : part))
        .join('-');

      formattedData.push({
        name: formattedName,
        baseline: Number(metrics.baseline) || 0,
        forecasted: Number(metrics.forecast) || 0,
        acceptedClient: Number(metrics.accepted_client) || 0,
      });
    });
  });

  return formattedData;
};

export default function LineBarChart({
  forecast_data,
  symbol,
}: {
  forecast_data: any;
  symbol: string | null | undefined;
}) {
  const chartData = React.useMemo(() => transformForecastData(forecast_data), [forecast_data]);

  // Updated formatters using the correct dynamic symbol variable
  const formatYAxis = (tickItem: number) => {
    // If the value is very small but not 0, show decimals. Otherwise show whole numbers.
    const decimals = tickItem > 0 && tickItem < 1000000 ? 2 : 0;
    return `${(tickItem / 1000000).toFixed(decimals)}M ${symbol}`;
  };

  const [hiddenLines, setHiddenLines] = useState<{ [key: string]: boolean }>({
    baseline: false,
    forecasted: false,
    acceptedClient: false,
  });

  const handleLegendClick = (e: any) => {
    const { dataKey } = e;
    setHiddenLines((prev) => ({
      ...prev,
      [dataKey]: !prev[dataKey],
    }));
  };

  const isEmpty = !chartData || chartData.length === 0;

  // Creates standard axis positions when data is missing
  const finalChartData = isEmpty ? [] : chartData;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={finalChartData} margin={{ top: 10, left: -15, right: 10 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isEmpty ? '#e0e0e0' : 'none'} />

        <XAxis
          dataKey="name"
          tick={{ fill: '#333', fontWeight: 500, fontSize: 11 }}
          textAnchor={chartData?.length > 10 ? 'end' : 'middle'}
          angle={chartData?.length > 10 ? -45 : 0}
          height={50}
          axisLine={{ stroke: '#ccc', strokeWidth: 1 }}
          tickLine={false}
        />

        <YAxis
          tickFormatter={formatYAxis}
          width={80}
          tick={{ fill: '#333', fontWeight: 500, fontSize: 11 }}
          axisLine={{ stroke: '#ccc', strokeWidth: 1 }}
          tickLine={false}
          domain={isEmpty ? [0, 5000000] : ['auto', 'auto']}
        />

        {!isEmpty && (
          <Tooltip
            formatter={(value) => [
              // Uses space separation formatting similar to your utility function
              `${Number(value)
                .toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} ${symbol}`,
            ]}
          />
        )}

        <Legend
          verticalAlign="bottom"
          align="center"
          iconType="circle"
          iconSize={8}
          wrapperStyle={{ fontSize: '13px', cursor: 'pointer' }}
          onClick={handleLegendClick}
        />

        <Line
          name="Baseline"
          type="linear"
          dataKey="baseline"
          stroke={isEmpty ? 'transparent' : '#6cd3b6'}
          strokeWidth={3}
          dot={false}
          activeDot={false}
          hide={hiddenLines.baseline}
        />
        <Line
          name="Forecasted"
          type="linear"
          dataKey="forecasted"
          stroke={isEmpty ? 'transparent' : '#ffb900'}
          strokeWidth={3}
          dot={false}
          activeDot={false}
          hide={hiddenLines.forecasted || isEmpty}
        />
        <Line
          name="Accepted Client"
          type="linear"
          dataKey="acceptedClient"
          stroke={isEmpty ? 'transparent' : '#ff4d4d'}
          strokeWidth={3}
          dot={false}
          activeDot={false}
          hide={hiddenLines.acceptedClient || isEmpty}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}
